import { useCallback, useMemo, useState } from 'react';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { createCustomColumn, useTableState } from '@medprompt/shared';
import {
  cloneCodeRecord,
  codeRecordFromSnomedSearchResult,
  codeRecordsEqual,
  normalizeCodeMap,
  normalizeFetchedCodeRecord,
} from './configCodes';
import { renderOptionalCell, renderSnomedCodeCell } from './configTableCells';

const useConfiguredCodeList = ({
  tableId,
  baselineCodes,
  isEditing,
  saving,
  addNotification,
  duplicateNotificationId,
  duplicateMessage,
  bulkActionKey,
}) => {
  const [codes, setCodes] = useState({});
  const [changes, setChanges] = useState({});
  const [defaultCode, setDefaultCode] = useState(null);
  const [editingDefault, setEditingDefault] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkDeleteRows, setBulkDeleteRows] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [editingCode, setEditingCode] = useState(null);

  const {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
    highlightedRows,
    setHighlightedRows,
  } = useTableState(tableId, {
    defaultSortState: { columnKey: 'preferredTerm', direction: 'asc' }
  });

  const applyFetched = useCallback((rawMap, rawDefault) => {
    const map = normalizeCodeMap(rawMap);
    const def = normalizeFetchedCodeRecord(rawDefault);
    setCodes(map);
    setChanges({});
    setDefaultCode(def);
    setEditingDefault(false);
    setModalOpen(false);
    setBulkDeleteOpen(false);
    setBulkDeleteRows([]);
    setSelectedRows(new Set());
    setEditingCode(null);
    return {
      codes: { ...map },
      defaultCode: cloneCodeRecord(def),
    };
  }, []);

  const snapshot = useCallback(() => ({
    codes: { ...codes },
    defaultCode: cloneCodeRecord(defaultCode),
  }), [codes, defaultCode]);

  const restore = useCallback((savedCodes, savedDefault) => {
    setCodes({ ...(savedCodes || {}) });
    setChanges({});
    setDefaultCode(cloneCodeRecord(savedDefault));
    setEditingDefault(false);
    setEditingCode(null);
    setModalOpen(false);
    setBulkDeleteOpen(false);
    setBulkDeleteRows([]);
    setSelectedRows(new Set());
  }, []);

  const closeEditors = useCallback(() => {
    setEditingDefault(false);
    setModalOpen(false);
    setBulkDeleteOpen(false);
    setBulkDeleteRows([]);
    setSelectedRows(new Set());
    setEditingCode(null);
  }, []);

  const clearChanges = useCallback(() => {
    setChanges({});
    setEditingCode(null);
    setSelectedRows(new Set());
    setEditingDefault(false);
  }, []);

  const removeCode = useCallback((codeId) => {
    setCodes((prevCodes) => {
      const { [codeId]: _removed, ...rest } = prevCodes;
      return rest;
    });
    setChanges((prevCh) => {
      const next = { ...prevCh };
      if (baselineCodes?.[codeId]) {
        next[codeId] = null;
      } else {
        delete next[codeId];
      }
      return next;
    });
  }, [baselineCodes]);

  const confirmBulkRemove = () => {
    bulkDeleteRows.forEach((row) => removeCode(row.code));
    setBulkDeleteOpen(false);
    setBulkDeleteRows([]);
    setSelectedRows(new Set());
  };

  const handleModalPick = (searchResultRow) => {
    const normalized = codeRecordFromSnomedSearchResult(searchResultRow);
    if (!normalized) return;
    if (codes[normalized.code]) {
      addNotification(duplicateNotificationId, duplicateMessage, { isError: true });
      return;
    }
    setCodes((prev) => ({ ...prev, [normalized.code]: normalized }));
    setChanges((prevCh) => ({ ...prevCh, [normalized.code]: normalized }));
    setModalOpen(false);
  };

  const handleDefaultSelect = (searchResultRow) => {
    const normalized = codeRecordFromSnomedSearchResult(searchResultRow);
    if (!normalized) return;
    setDefaultCode(normalized);
    setEditingDefault(false);
  };

  const handleBulkPrepareDelete = useCallback((selectedRowsData) => {
    if (!selectedRowsData?.length) return;
    setBulkDeleteRows(selectedRowsData);
    setBulkDeleteOpen(true);
  }, []);

  const handleRowClick = useCallback((row) => {
    if (!isEditing || saving) return;
    setEditingCode(row.code);
  }, [isEditing, saving]);

  const handleCodeSave = useCallback((updatedRecord) => {
    setCodes((prev) => ({ ...prev, [updatedRecord.code]: updatedRecord }));
    setChanges((prevCh) => {
      const next = { ...prevCh };
      const baseline = baselineCodes?.[updatedRecord.code];
      if (baseline && codeRecordsEqual(updatedRecord, baseline)) {
        delete next[updatedRecord.code];
      } else {
        next[updatedRecord.code] = updatedRecord;
      }
      return next;
    });
    setEditingCode(null);
  }, [baselineCodes]);

  const bulkActions = useMemo(
    () => [
      {
        key: bulkActionKey,
        label: 'Remove from list',
        icon: faTrash,
        onClick: handleBulkPrepareDelete
      }
    ],
    [bulkActionKey, handleBulkPrepareDelete]
  );

  const tableRows = useMemo(
    () =>
      Object.entries(codes).map(([id, record]) => {
        const preferredTerm =
          Array.isArray(record.terms) && record.terms.length > 0 ? record.terms[0] : '';
        return {
          code: id,
          preferredTerm
        };
      }),
    [codes]
  );

  const columns = useMemo(
    () => [
      createCustomColumn(
        'code',
        'SNOMED CT code',
        (row) => row.code ?? '',
        renderSnomedCodeCell,
        { sortable: true, filterable: true }
      ),
      createCustomColumn(
        'preferredTerm',
        'Term',
        (row) => row.preferredTerm ?? '',
        renderOptionalCell,
        { sortable: true, filterable: true }
      )
    ],
    []
  );

  return {
    tableId,
    codes,
    changes,
    defaultCode,
    editingDefault,
    setEditingDefault,
    modalOpen,
    setModalOpen,
    bulkDeleteRows,
    setBulkDeleteRows,
    bulkDeleteOpen,
    setBulkDeleteOpen,
    selectedRows,
    setSelectedRows,
    editingCode,
    setEditingCode,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
    highlightedRows,
    setHighlightedRows,
    tableRows,
    columns,
    bulkActions,
    handleModalPick,
    handleDefaultSelect,
    handleRowClick,
    handleCodeSave,
    confirmBulkRemove,
    applyFetched,
    snapshot,
    restore,
    closeEditors,
    clearChanges,
  };
};

export default useConfiguredCodeList;
